import { NodeViewWrapper } from "@tiptap/react";
import { t } from "@/i18n-js/instance";
import { useEnterOrSpaceKeyDownHandler } from "@circle-react/hooks/useEnterOrSpaceKeyDownHandler";
import { useElectronAPI } from "@circle-react/hooks/utils/useElectronAPI";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { useBlockEditorContext } from "@circle-react-uikit/TipTapBlockEditor";
import { getEntityContent } from "./helpers";

interface Entity {
  comment?: string;
  name: string;
  type: string;
  url: string;
}

export const EntityDisplay = ({ node }: any) => {
  const { navigateToUrl, isElectron } = useElectronAPI();
  const tiptapBlockEditorContext = useBlockEditorContext();
  const tiptapEditorContext = useTipTapEditorContext();

  const { sgidToObjectMap = {}, localSgidToObjectMap = {} } =
    tiptapEditorContext || tiptapBlockEditorContext;

  const entity: Entity =
    localSgidToObjectMap[node.attrs.sgid] || sgidToObjectMap[node.attrs.sgid];

  const redirectToEntity = () => {
    const path = entity.url;
    if (isElectron) {
      void navigateToUrl(path);
    } else {
      window.open(path, "_blank", "noreferrer");
    }
  };

  const onKeyDownHandler = useEnterOrSpaceKeyDownHandler(redirectToEntity);

  if (!entity) {
    return (
      <NodeViewWrapper as="span">
        <TippyV2
          interactive={false}
          content={t("unavailable_entity_message")}
          placement="top"
        >
          <span className="text-circle mx-0.5 cursor-pointer rounded-sm bg-indigo-100 px-0.5 font-medium">
            {t("unavailable_entity")}
          </span>
        </TippyV2>
      </NodeViewWrapper>
    );
  }

  const isSpaceEntity = entity.type === "space" || entity.type === "Space";

  const entityContent = getEntityContent(entity);

  return (
    <NodeViewWrapper as="span">
      <TippyV2
        interactive={false}
        content={entityContent}
        placement="top"
        disabled={!entityContent}
      >
        {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- This component can be a child of a <s> tag */}
        <span
          onClick={redirectToEntity}
          onKeyDown={onKeyDownHandler}
          role="button"
          contentEditable={false}
          className="text-circle mx-0.5 cursor-pointer rounded-sm bg-indigo-100 px-0.5 text-left font-medium"
          tabIndex={0}
        >
          {isSpaceEntity && "#"}
          {entity.name || t("tiptap.entity_untitled")}
        </span>
      </TippyV2>
    </NodeViewWrapper>
  );
};
